import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { StepName } from "../../../models/stepper";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { addForm, clearDocumentState } from "../../../store/reducers/document-metadata";
import { clearDossierState } from "../../../store/reducers/dossier-metadata";
import { resetStepperState, setCurrentStep } from "../../../store/reducers/stepper";
import { selectMetadata, selectStepper } from "../../../store/selectors";
import { StepperConfig } from "../../../utils/stepperconfig";
import { DefaultLayout, SidebarLayout } from "../../layouts";
import { PopupAnnuleerDossierMolecule, StepperMolecule } from "../../molecules";
import {
  DocumentFormulierOrganism,
  OverheidHeaderOrganism,
  PublicatieStepsContentOrganism,
  SidebarOrganism,
  ToastOrganism,
} from "../../organisms";

export default function CreateScreen() {
  const { currentStep, completedSteps, flow } = useAppSelector(selectStepper);
  const { documentError } = useAppSelector(selectMetadata);
  const steps = useSelector(StepperConfig);
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [popupAnnuleren, setPopupAnnuleren] = useState(false);
  const documentErrorIndexListRef = useRef<{ index: number; setIndex: number }[] | undefined>(documentError?.indexList);
  const hasDocumentErrorsRef = useRef<boolean>(!!documentError?.indexList?.length || !!documentError?.pid?.length);

  const paramId = process.env.REACT_APP_DOSSIERS === "true" && !id;

  useEffect(() => {
    documentErrorIndexListRef.current = documentError?.indexList;
    hasDocumentErrorsRef.current = !!documentError?.indexList?.length || !!documentError?.pid?.length;
  }, [documentError]);

  const reset = useCallback(() => {
    dispatch(resetStepperState());
    dispatch(clearDossierState());
    dispatch(clearDocumentState());

    if (!paramId) dispatch(addForm());
  }, [dispatch, paramId]);

  useEffect(() => {
    return () => {
      const currentIndexList = documentErrorIndexListRef.current;
      const hasErrors = hasDocumentErrorsRef.current;
      if (hasErrors || (currentIndexList && currentIndexList.length > 0)) return;
      reset();
    };
  }, [id, reset]);

  const header = (
    <>
      <ToastOrganism />
      <OverheidHeaderOrganism />
      {paramId && (
        <StepperMolecule
          steps={steps}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onClick={(index: number) => {
            if (index === 0 && flow === "Dossier") setPopupAnnuleren(true);
            else if (index === 0 && flow === "Normal") {
              reset();
              dispatch(setCurrentStep(index));
            } else if (index !== 0) dispatch(setCurrentStep(index));
          }}
        />
      )}
    </>
  );

  const body = (
    <>
      {popupAnnuleren && (
        <PopupAnnuleerDossierMolecule
          close={() => setPopupAnnuleren(false)}
          dossierAnnuleren={() => {
            reset();
            dispatch(setCurrentStep(0));
          }}
        />
      )}
      {paramId ? <PublicatieStepsContentOrganism /> : <DocumentFormulierOrganism />}
    </>
  );
  const noSidebar = paramId && (["Kies type"] as StepName[]).includes(steps[currentStep]);

  if (noSidebar) return <DefaultLayout header={header} body={body} />;
  return <SidebarLayout sidebar={<SidebarOrganism />} header={header} body={body} />;
}
